import { accountTypeLevel } from '../constants/accountTypes';

/**
 * Determine if permission-based checks should be used
 * Custom roles use permission checks, system roles use level checks
 * @param {Object} member - Member object
 * @returns {boolean}
 */
export const shouldUsePermissionCheck = (member) => {
  // If custom role, use permission checks
  if (member?.role && member.role.isSystemRole === false) {
    return true;
  }
  return false;
};

/**
 * Get member's role level (supports both new role system and legacy accountType)
 * @param {Object} member - Member object
 * @returns {number} Role level (0-5)
 */
export const getMemberRoleLevel = (member) => {
  // If member has role object (new system), use that
  if (member?.role && member.role.level !== undefined) {
    return member.role.level;
  }
  // Fall back to accountType (old system)
  return accountTypeLevel[member?.accountType] || 0;
};

/**
 * Check if member has a system role
 * @param {Object} member - Member object
 * @returns {boolean}
 */
export const isSystemRole = (member) => {
  if (member?.role && member.role.isSystemRole !== undefined) {
    return member.role.isSystemRole;
  }
  // If no role object or old system, treat as system role for backward compatibility
  return true;
};

/**
 * Get all permission names for a member
 * Checks member object first, then falls back to localStorage
 * @param {Object} member - Member object (optional)
 * @returns {string[]} Array of permission names
 */
export const getMemberPermissions = (member) => {
  // Try to get from member object first
  if (member?.role?.permissions) {
    return member.role.permissions.map((p) => p.name);
  }

  // Fall back to localStorage
  try {
    const storedPermissions = localStorage.getItem('shoot_permissions');
    if (storedPermissions) {
      return JSON.parse(storedPermissions);
    }
  } catch (e) {
    console.error('Error reading permissions from localStorage:', e);
  }

  return [];
};

/**
 * Check if member has any of the specified permissions
 * @param {Object} member - Member object
 * @param {string[]} permissionNames - Array of permission names
 * @returns {boolean}
 */
export const memberHasAnyPermission = (member, permissionNames) => {
  if (member?.role?.permissions) {
    const memberPermissions = member.role.permissions.map((p) => p.name);
    return permissionNames.some((name) => memberPermissions.includes(name));
  }
  return false;
};

/**
 * Check if member is admin (Workspace admin or higher)
 * @param {Object} member - Member object
 * @returns {boolean}
 */
export const isMemberAdmin = (member) => {
  const level = getMemberRoleLevel(member);
  return level >= accountTypeLevel['Workspace admin'];
};

/**
 * Check if member has specific permission
 * For custom roles: checks permission list
 * For system roles: always returns true to maintain backward compatibility
 * @param {Object} member - Member object
 * @param {string} permissionName - Permission name (e.g., 'member:create')
 * @returns {boolean}
 */
export const memberHasPermission = (member, permissionName) => {
  // System roles: use level-based access (backward compatible)
  if (isSystemRole(member)) {
    return true; // System roles handled by level checks elsewhere
  }

  // Custom roles: check specific permissions
  const permissions = getMemberPermissions(member);
  return permissions.includes(permissionName);
};

/**
 * Check if member has all of the specified permissions
 * @param {Object} member - Member object
 * @param {string[]} permissionNames - Array of permission names
 * @returns {boolean}
 */
export const memberHasAllPermissions = (member, permissionNames) => {
  if (member?.role?.permissions) {
    const memberPermissions = member.role.permissions.map((p) => p.name);
    return permissionNames.every((name) => memberPermissions.includes(name));
  }
  return false;
};

/**
 * Check if member has required role level
 * @param {Object} member - Member object
 * @param {string} requiredAccountType - Required account type (e.g., 'Workspace admin')
 * @returns {boolean}
 */
export const memberHasRoleLevel = (member, requiredAccountType) => {
  const memberLevel = getMemberRoleLevel(member);
  const requiredLevel = accountTypeLevel[requiredAccountType] || 0;
  return memberLevel >= requiredLevel;
};

/**
 * Check if member can access a resource based on permission OR role level
 * System roles: Use role level check
 * Custom roles: Use permission check
 * @param {Object} member - Member object
 * @param {string} permissionName - Permission to check
 * @param {string} fallbackAccountType - Fallback account type for legacy check
 * @returns {boolean}
 */
export const memberCanAccess = (member, permissionName, fallbackAccountType) => {
  // System roles: use level-based check
  if (isSystemRole(member)) {
    if (fallbackAccountType) {
      return memberHasRoleLevel(member, fallbackAccountType);
    }
    return true;
  }

  // Custom roles: use permission-based check
  if (permissionName && memberHasPermission(member, permissionName)) {
    return true;
  }

  // Fallback to role level check if no permission specified
  if (fallbackAccountType) {
    return memberHasRoleLevel(member, fallbackAccountType);
  }

  return false;
};

/**
 * Filter array of items based on member permissions
 * @param {Array} items - Items to filter
 * @param {Object} member - Member object
 * @param {Function} permissionCheck - Function that takes (item, member) and returns boolean
 * @returns {Array} Filtered items
 */
export const filterByPermission = (
  items,
  member,
  permissionCheck,
) => items.filter((item) => permissionCheck(item, member));

/**
 * Route access checker
 * System roles: Use accountLevel check
 * Custom roles: Use permission check with accountLevel fallback
 * @param {string} requiredPermission - Required permission for custom roles
 * @param {number} accountLevel - Required account level
 * @param {Object} member - Member object
 * @returns {boolean}
 */
export const canAccessRoute = (requiredPermission, accountLevel, member) => {
  const isSystemRoleMember = isSystemRole(member);
  const memberLevel = getMemberRoleLevel(member);

  // System roles: use level-based checks (existing behavior)
  if (isSystemRoleMember) {
    if (accountLevel === 2) {
      // Team Leader or Reporting Manager level
      const isTeamLeader = memberLevel === accountTypeLevel['Team Leader'];
      return memberLevel >= accountTypeLevel['Workspace admin'] || isTeamLeader;
    }

    // Default: check if member level meets requirement. Level 3 is Workspace
    // admin, so it admits Workspace admin, Workspace owner and Primary owner;
    // a page meant for the Primary owner alone asks for level 5.
    return memberLevel >= accountLevel;
  }

  // Custom roles: use permission-based checks
  if (requiredPermission && memberHasPermission(member, requiredPermission)) {
    return true;
  }

  // Fallback to level check
  return memberLevel >= accountLevel;
};

/**
 * Navigation item permission checker
 * System roles: Check based on accountLevel (backward compatible)
 * Custom roles: Check based on permission field
 * @param {Object} navItem - Navigation item with optional permission or accountLevel
 * @param {Object} member - Member object
 * @returns {boolean}
 */
export const canAccessNavItem = (navItem, member) => {
  const isSystemRoleMember = isSystemRole(member);
  const memberLevel = getMemberRoleLevel(member);

  // System roles: use level-based checks (existing behavior)
  if (isSystemRoleMember) {
    // If nav item has account level requirement
    if (navItem.accountLevel !== undefined) {
      // Handle special cases
      if (navItem.accountLevel === 2) {
        // Team Leader or Reporting Manager
        const isTeamLeader = memberLevel === accountTypeLevel['Team Leader'];
        const isReportingMgr = member?.isReportingManager;
        return memberLevel >= accountTypeLevel['Workspace admin'] || isTeamLeader || isReportingMgr;
      }

      // Default: check if member level meets requirement. Level 3 is Workspace
      // admin and above; Primary-owner-only items ask for level 5.
      return memberLevel >= navItem.accountLevel;
    }

    // No specific level requirement for system roles
    return true;
  }

  // Custom roles: use permission-based checks
  // If nav item has required permission, check it
  if (navItem.permission) {
    return memberHasPermission(member, navItem.permission);
  }

  // If no permission specified but has accountLevel, use that as fallback
  if (navItem.accountLevel !== undefined) {
    // For custom roles, still respect level if no specific permission
    return memberLevel >= navItem.accountLevel;
  }

  // If no specific requirements, allow access
  return true;
};

export default {
  isSystemRole,
  shouldUsePermissionCheck,
  getMemberRoleLevel,
  isMemberAdmin,
  memberHasPermission,
  memberHasAnyPermission,
  memberHasAllPermissions,
  memberHasRoleLevel,
  memberCanAccess,
  getMemberPermissions,
  filterByPermission,
  canAccessNavItem,
  canAccessRoute,
};
